/**
 * Jump list for the parts of the road, with how far through each you are.
 *
 * The road is 81 stops and roughly 24,000 pixels long. Without this the only
 * way to reach Phase Four is to scroll past Phases One to Three, and the only
 * way to know you had finished a part was to count.
 */

import { useMemo } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { groupBySection } from "@/lib/catalog/index.ts";
import { useFilteredRoad } from "@/lib/filters/context.tsx";
import { sectionProgress } from "@/lib/road/progress.ts";
import { useProgressStore } from "@/state/progress.ts";
import styles from "./SectionNav.module.css";

export function SectionNav() {
  const catalog = useCatalog();
  const { visible } = useFilteredRoad();
  const watched = useProgressStore((s) => s.watched);

  const parts = useMemo(
    () => sectionProgress(groupBySection(catalog, visible), watched),
    [catalog, visible, watched],
  );

  if (parts.length === 0) return null;

  const jump = (sectionId: string) => {
    const el = document.getElementById(`${sectionId}-heading`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.setAttribute("tabindex", "-1");
    el.focus({ preventScroll: true });
  };

  return (
    <nav className={styles.nav} aria-label="Parts of the road">
      <h2 className={styles.heading}>Parts</h2>
      <ol className={styles.list}>
        {parts.map(({ section, entries, watched: done, fraction }) => (
          <li key={section.id}>
            <button
              type="button"
              className={styles.item}
              onClick={() => jump(section.id)}
              data-done={done === entries.length && entries.length > 0}
            >
              <span className={styles.title}>{section.title}</span>
              {/* aria-hidden: the count beside it already says this in words,
                  and a bar announced as a number is noise. */}
              <span
                className={styles.bar}
                aria-hidden="true"
                style={{ "--fill": `${Math.round(fraction * 100)}%` } as React.CSSProperties}
              />
              <span className={styles.count}>
                {done}/{entries.length}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}
